import { FC, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { Box, Button, Typography } from '@mui/material'

import { WalletsPaymentSystem, WalletType } from 'types/wallet'

import { getDefaultName } from 'utils/text'

import { useCreateOrganizationWalletMutation } from 'services/organizations'
import { useCreateWalletMutation, useGetAvailableWalletsQuery } from 'services/wallets'

import TableButton from 'components/AddWalletModal/TableButton'
import Modal from 'components/Modal'
import Preloader from 'components/Preloader'
import ScrollableBox from 'components/ScrollableBox'

import StepsLinearProgress from './StepsLinearProgress'
import s from './styles'

interface Props {
  handleClose: () => void
  organizationId?: string
  refetchOrganizationWallets?: (activeCurrency: string) => void
}
const AddWalletModal: FC<Props> = ({ handleClose, organizationId, refetchOrganizationWallets }) => {
  const [, setSearchParams] = useSearchParams()

  const [step, setStep] = useState<number>(1)
  const [activeCurrency, setActiveCurrency] = useState<string | null>(null)
  const [activeWalletType, setActiveWalletType] = useState<WalletType | null>(null)
  const [activePaymentSystem, setActivePaymentSystem] = useState<WalletsPaymentSystem | null>(null)
  const isFirstStep = step === 1

  const [addWallet, { isLoading }] = useCreateWalletMutation()
  const [addOrgWallet, { isLoading: isOrgLoading }] = useCreateOrganizationWalletMutation()
  const { data: availableWallets, isLoading: isLoadingWalletTypes } = useGetAvailableWalletsQuery()

  const handlePaymentSystemChange = (
    paymentSystem: WalletsPaymentSystem,
    walletType: WalletType
  ) => {
    setActivePaymentSystem(paymentSystem)
    setActiveWalletType(walletType)
  }

  const handleCurrencyChange = (currency: string) => {
    setActiveCurrency(currency)
  }

  const handleStepChange = () => {
    setStep(isFirstStep ? 2 : 1)
  }

  // temporary solution until the list is fixed on the backend
  const availableWalletsExceptCw =
    availableWallets &&
    Object.fromEntries(Object.entries(availableWallets).filter(([key]) => key !== 'cw'))

  const onSubmit = () => {
    if (activeWalletType && activeCurrency) {
      const body = {
        currency: activeCurrency,
        name: getDefaultName(activeCurrency),
        isDefault: false,
        walletType: activeWalletType,
      }

      organizationId
        ? addOrgWallet({ body, organizationId })
            .unwrap()
            .then(() => {
              if (activeCurrency && refetchOrganizationWallets) {
                refetchOrganizationWallets(activeCurrency)
              }
              handleClose()
            })
        : addWallet(body)
            .unwrap()
            .then((resp) => {
              handleClose()
              setSearchParams((prevParams) => {
                const params = new URLSearchParams(prevParams)
                params.set('wallet', resp.id)
                params.set('currency', resp.currency)
                params.set('excludeEmpty', 'false')
                return params
              })
            })
    }
  }

  const isActionButtonDisabled =
    isLoading ||
    isOrgLoading ||
    (isFirstStep && !activePaymentSystem) ||
    (!isFirstStep && !activeCurrency)

  if (isLoadingWalletTypes) return <Preloader />
  return (
    <Modal open onClose={handleClose} handleClose={handleClose} title="Add new wallet">
      <Box sx={s.container}>
        <StepsLinearProgress isFirstStep={isFirstStep} />
        <Box sx={s.content}>
          {availableWalletsExceptCw && (
            <>
              {isFirstStep ? (
                <Box sx={s.fitstStepBox}>
                  <Typography variant="h5" sx={s.selectPaymentText}>
                    Select a payment system
                  </Typography>
                  <ScrollableBox sx={s.tabsWrapper}>
                    {Object.entries(availableWalletsExceptCw).map(([paymentSystem, content]) => (
                      <TableButton
                        key={paymentSystem}
                        currency={paymentSystem}
                        isPaymentSystem
                        isActiveWallet={activePaymentSystem === paymentSystem}
                        handleClick={() =>
                          handlePaymentSystemChange(
                            paymentSystem as WalletsPaymentSystem,
                            content.walletType
                          )
                        }
                      />
                    ))}
                  </ScrollableBox>
                </Box>
              ) : (
                <>
                  <ScrollableBox sx={s.tabsWrapper}>
                    {activePaymentSystem &&
                    availableWallets[activePaymentSystem].supportedCurrencies?.length ? (
                      availableWallets[activePaymentSystem].supportedCurrencies.map((currency) => (
                        <TableButton
                          key={currency}
                          currency={currency}
                          isActiveWallet={activeCurrency === currency}
                          handleClick={() => handleCurrencyChange(currency)}
                        />
                      ))
                    ) : (
                      <Typography alignSelf="center">Wallets are not available</Typography>
                    )}
                  </ScrollableBox>
                </>
              )}
            </>
          )}
        </Box>

        <Box sx={s.buttonsContainer}>
          {!isFirstStep && (
            <Button variant="outlined" onClick={handleStepChange} sx={s.button}>
              Back
            </Button>
          )}
          <Button
            variant="contained"
            onClick={isFirstStep ? handleStepChange : onSubmit}
            sx={s.button}
            disabled={isActionButtonDisabled}
          >
            {isFirstStep ? 'Next' : 'Create'}
          </Button>
        </Box>
      </Box>
    </Modal>
  )
}

export default AddWalletModal
